import { Suspense, lazy, useCallback, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";
import { setMeta } from "./docs/meta";
import type { Meta, SessionUser } from "./types";
import Login from "./components/Login";
import PatientsPage from "./components/PatientsPage";
// Разделы грузятся при первом заходе: стартовой странице (картотеке)
// оперсписок с записью и админка не нужны.
const OpListsPage = lazy(() => import("./components/OpListsPage"));
const ApptsPage = lazy(() => import("./components/ApptsPage"));
const AdminPage = lazy(() => import("./components/AdminPage"));
const StockPage = lazy(() => import("./components/StockPage"));
const SystemPage = lazy(() => import("./components/SystemPage"));
import TopBar, { type MobileSearch, type Section } from "./components/TopBar";
import { SectionActive } from "./live";
import { systemPageAt } from "./components/system-pages";
import { MessageProvider } from "./components/Message";

/**
 * Раздел приложения. Скрытый (уже открывавшийся) остаётся смонтированным, но
 * не перечитывает данные по событиям — см. live.ts; код раздела грузится при
 * первом открытии. Объявлен вне App: компонент, объявленный внутри, — новый
 * тип на каждом рендере App, и React пересоздавал бы все разделы с нуля при
 * каждом переключении (поиск, прокрутка, открытые формы терялись).
 */
function Sect({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <SectionActive.Provider value={on}>
      <div className={`app-sect${on ? "" : " app-sect-off"}`}>
        <Suspense fallback={<div className="op-empty">Загружаем…</div>}>{children}</Suspense>
      </div>
    </SectionActive.Provider>
  );
}

export default function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [section, setSection] = useState<Section>("patients");
  // Кнопки мобильной шапки (поиск и «+») живут в TopBar, а состояние — на
  // странице пациентов; связь — через эти «толчки»: n растёт, страница ловит.
  const [extSearch, setExtSearch] = useState<{ f: MobileSearch; n: number } | null>(null);
  const [extAdd, setExtAdd] = useState(0);
  // «Карточка пациента» из записи и оперсписка: переключить раздел и открыть.
  // «Карта в новой вкладке» из оперсписка: /?pat=ID открывает раздел
  // «Пациенты» сразу на этом пациенте.
  const [extPatient, setExtPatient] = useState<{ id: number; n: number } | null>(() => {
    const id = Number(new URLSearchParams(window.location.search).get("pat"));
    return id > 0 ? { id, n: 1 } : null;
  });
  // Разделы не размонтируются при переключении: страница прячется, а её
  // состояние — найденный пациент, место в календаре, прокрутка, недописанные
  // поля — остаётся как было. Монтируем лениво: только то, куда уже заходили.
  const [visited, setVisited] = useState<Section[]>(["patients"]);
  // Повторный клик по уже открытой вкладке — сброс её к начальному виду:
  // смена key размонтирует страницу и соберёт заново.
  const [resetN, setResetN] = useState<Record<Section, number>>({
    patients: 0,
    operations: 0,
    stock: 0,
    appointments: 0,
    admin: 0,
  });
  const [meta, setMetaState] = useState<Meta | null>(null);
  const [booting, setBooting] = useState(true);

  const loadSession = useCallback(async () => {
    try {
      const { user } = await api.me();
      const m = await api.meta();
      setMeta(m);
      setMetaState(m);
      setUser(user);
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) console.error(err);
      setUser(null);
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  if (booting) {
    return (
      <div className="container-center">
        <div className="boot-loader" />
      </div>
    );
  }

  if (!user || !meta) return <Login onLoggedIn={loadSession} />;

  const logout = async () => {
    await api.logout();
    setUser(null);
  };

  // «Маршруты» вне разделов — /update и /ai: страницы системного
  // администратора, чтобы их можно было открыть по ссылке, не заходя в админку.
  const systemPage = systemPageAt(window.location.pathname);
  if (systemPage) {
    return (
      <MessageProvider>
        <Suspense fallback={<div className="op-empty">Загружаем…</div>}>
          <SystemPage kind={systemPage} user={user} onLogout={logout} />
        </Suspense>
      </MessageProvider>
    );
  }

  // The operations register is gated on the same right the API checks, so an
  // account without it never sees a tab it would only be refused on. Страница
  // администратора — так же: не-админ, попавший на неё, оказывается на главной.
  const openPatient = (id: number) => {
    setSection("patients");
    setExtPatient((p) => ({ id, n: (p?.n ?? 0) + 1 }));
  };

  const canOperations = user.can_op_list || user.is_admin;
  let active: Section = section;
  if (active === "operations" && !canOperations) active = "patients";
  if (active === "admin" && !user.is_admin) active = "patients";

  const switchSection = (s: Section) => {
    // Оперсписок — всегда свежий: его правят несколько человек одновременно,
    // и вернуться на вкладку значит перечитать список, а не смотреть старое.
    if (s === active || s === "operations") {
      setResetN((p) => ({ ...p, [s]: p[s] + 1 }));
      if (s === active) return;
    }
    setSection(s);
    setVisited((v) => (v.includes(s) ? v : [...v, s]));
  };

  /** Раздел смонтирован, если открыт сейчас или уже открывался. */
  const mounted = (s: Section) => s === active || visited.includes(s);

  return (
    <MessageProvider>
      <div className="container-center container-center-logged">
        <TopBar
          user={user}
          meta={meta}
          section={active}
          onSection={switchSection}
          onLogout={logout}
          onMobileSearch={(f) => {
            setSection("patients");
            setExtSearch((p) => ({ f, n: (p?.n ?? 0) + 1 }));
          }}
          onMobileAdd={() => {
            setSection("patients");
            setExtAdd((v) => v + 1);
          }}
          onOpenPatient={openPatient}
        />
        {mounted("patients") && (
          <Sect on={active === "patients"}>
            <PatientsPage
              key={resetN.patients}
              user={user}
              meta={meta}
              extSearch={extSearch}
              extAdd={extAdd}
              extPatient={extPatient}
            />
          </Sect>
        )}
        {canOperations && mounted("operations") && (
          <Sect on={active === "operations"}>
            <OpListsPage key={resetN.operations} user={user} meta={meta} onOpenPatient={openPatient} />
          </Sect>
        )}
        {mounted("stock") && (
          <Sect on={active === "stock"}>
            <StockPage key={resetN.stock} user={user} />
          </Sect>
        )}
        {mounted("appointments") && (
          <Sect on={active === "appointments"}>
            <ApptsPage key={resetN.appointments} user={user} onOpenPatient={openPatient} />
          </Sect>
        )}
        {user.is_admin && mounted("admin") && (
          <Sect on={active === "admin"}>
            <AdminPage key={resetN.admin} user={user} />
          </Sect>
        )}
      </div>
    </MessageProvider>
  );
}
